import { useSearchParams } from 'react-router-dom';
import { SkeletonProps, Skeleton } from 'components/elements/Skeleton';
import { UseGetResult } from 'hooks/useGet';
import { useQParam } from 'hooks/useParams';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';

type UseModalProps<T extends any> = {
  queryParam: keyof typeof QUERY_PARAM;
  dataHook(id: string): UseGetResult<T>;
};

export type UseModalResult<T extends object> = {
  model: UseGetResult<T>;
  onClose(): void;
  isOpen: boolean;
  Skeleton: (props: Omit<SkeletonProps, 'isLoaded'>) => JSX.Element;
};

export function useModal<T extends object>({
  queryParam,
  dataHook,
}: UseModalProps<T>): UseModalResult<T> {
  const id = useQParam(QUERY_PARAM[queryParam]);
  const model = dataHook(id!);

  return {
    model,
    isOpen: true,
    Skeleton: (props) => <Skeleton isLoaded={!model.loading} {...props} />,
    onClose: () => {
      window.history.back();
    },
  };
}

type UseModalActionResult = {
  onClose(): void;
  isOpen: boolean;
  Skeleton: (props: Omit<SkeletonProps, 'isLoaded'>) => JSX.Element;
};

export const useGetModalActions = (isLoading: boolean): UseModalActionResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  return {
    isOpen: true,
    Skeleton: (props) => <Skeleton isLoaded={!isLoading} {...props} />,
    onClose: () => {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        const newSearchParams = new URLSearchParams(searchParams.toString());
        // Remove the CURRENTLY SUPPORTED Document modal query parameters to ensure the modal closes
        newSearchParams.delete(QUERY_PARAM.documentType);
        newSearchParams.delete(QUERY_PARAM.documentId);

        // Remove the LEGACY Document modal query parameters to ensure the modal closes
        newSearchParams.delete(QUERY_PARAM.buildingPermit);
        newSearchParams.delete(QUERY_PARAM.buildingPlanReview);
        newSearchParams.delete(QUERY_PARAM.businessLicense);
        newSearchParams.delete(QUERY_PARAM.developmentPermit);
        newSearchParams.delete(QUERY_PARAM.accessibilityCompliance);
        newSearchParams.delete(QUERY_PARAM.electricalInspection);
        newSearchParams.delete(QUERY_PARAM.landUseRedesignation);
        newSearchParams.delete(QUERY_PARAM.mechanicalPermit);
        newSearchParams.delete(QUERY_PARAM.municipalHearing);
        newSearchParams.delete(QUERY_PARAM.plumbingPermit);
        newSearchParams.delete(QUERY_PARAM.plat);
        newSearchParams.delete(QUERY_PARAM.landTitleChange);

        setSearchParams(newSearchParams);
      }
    },
  };
};

export const useGetQueryParam = (queryParam: keyof typeof QUERY_PARAM) => {
  return useQParam(QUERY_PARAM[queryParam]);
};
